// =============================================================================
// journey-chain.ts – Pure logic to construct intermodal multi-segment timelines
// =============================================================================

import type { AppState, AppConfig, Direction, Trip } from "./types.js";
import { minsToHHMM } from "./utils.js";
import { getStopName } from "./line-config.js";
import { getWalkMinForStop } from "./walk-per-stop";
import {
  classifyConnection,
  getThresholdsFromState,
  ConnectionState
} from "./connection-classifier";
import { calcNextTrain } from "./trains";

export interface JourneyStep {
  type: "walk" | "bus" | "wait" | "train" | "metro";
  icon: string;
  label: string;
  location: string;
  timeHHMM: string;
  durationMin: number;
  connection?: {
    state: ConnectionState;
    waitMin: number;
    progressPercent: number;
  };
}

export interface JourneyChain {
  steps: JourneyStep[];
  totalMinutes: number;
  arrivalHHMM: string;
  primaryConnection: ConnectionState | null;
}

export function buildJourneyChain(
  card: any,
  direction: Direction,
  cfg: AppConfig,
  state: AppState,
  currentMin: number
): JourneyChain {
  const steps: JourneyStep[] = [];
  const trip = card.trip as Trip | undefined;
  if (!trip) {
    return { steps: [], totalMinutes: 0, arrivalHHMM: "--:--", primaryConnection: null };
  }

  const walkMin = getWalkMinForStop(card.fromStop, state, cfg);
  const busDepMin = trip._depMin ?? 0;
  const leaveMin = busDepMin - walkMin;
  let arrivalMin = busDepMin;

  // Step 1: Cammina
  steps.push({
    type: "walk",
    icon: "🚶",
    label: "A piedi",
    location: "Casa",
    timeHHMM: minsToHHMM(leaveMin),
    durationMin: walkMin
  });

  // Step 2: Bus
  steps.push({
    type: "bus",
    icon: "🚌",
    label: card.lineId || "Bus",
    location: getStopName(card.fromStop),
    timeHHMM: minsToHHMM(busDepMin),
    durationMin: 0
  });

  const thresholds = getThresholdsFromState(state, cfg);
  let primaryConnection: ConnectionState | null = null;
  const connectionStops = [...new Set([...(card.detailStops || []), ...(card.compactStops || [])])];

  for (const code of connectionStops) {
    const arrMin = trip.stops?.[code];
    const interchange = cfg.interchanges?.[code] as any;
    if (arrMin === undefined || arrMin === null || !interchange) continue;

    if (interchange.trainSlot) {
      const trains = calcNextTrain(interchange.trainSlot, arrMin);
      if (trains.length === 0) continue;
      const train = trains[0];
      const connState = classifyConnection(train.waitMin, thresholds);
      primaryConnection = connState;

      // Arrivo al nodo
      steps.push({
        type: "bus",
        icon: "📍",
        label: "Arrivo",
        location: getStopName(code),
        timeHHMM: minsToHHMM(arrMin),
        durationMin: arrMin - busDepMin
      });

      // Attesa
      if (train.waitMin > 0) {
        steps.push({
          type: "wait",
          icon: "⏱",
          label: "Attesa",
          location: interchange.label,
          timeHHMM: "",
          durationMin: train.waitMin,
          connection: {
            state: connState,
            waitMin: train.waitMin,
            progressPercent: Math.min(100, (train.waitMin / thresholds.longMin) * 100)
          }
        });
      }

      // Treno
      steps.push({
        type: "train",
        icon: "🚆",
        label: train.line,
        location: interchange.label,
        timeHHMM: minsToHHMM(train.departureMin),
        durationMin: 0
      });
      arrivalMin = train.departureMin;
      break; // solo prima coincidenza nella chain
    } else if (interchange.type === "M1") {
      // Arrivo al metro
      steps.push({
        type: "bus",
        icon: "📍",
        label: "Arrivo",
        location: getStopName(code),
        timeHHMM: minsToHHMM(arrMin),
        durationMin: arrMin - busDepMin
      });

      steps.push({
        type: "metro",
        icon: "Ⓜ️",
        label: "M1",
        location: interchange.label,
        timeHHMM: minsToHHMM(arrMin),
        durationMin: 0
      });
      arrivalMin = arrMin;
      break;
    }
  }

  // Se non c'è stata coincidenza intermodale, la fine della catena è la fine del viaggio bus stesso
  if (steps.length === 2) {
    const finalStops =
      direction === "return" ? card.returnHomeStops || [] : card.compactStops || [];
    let maxArrMin = busDepMin;
    let maxStopCode = card.fromStop;
    for (const code of finalStops) {
      const tStop = trip.stops?.[code];
      if (tStop !== undefined && tStop !== null && tStop > maxArrMin) {
        maxArrMin = tStop;
        maxStopCode = code;
      }
    }
    if (maxArrMin > busDepMin) {
      steps.push({
        type: "bus",
        icon: "📍",
        label: "Arrivo",
        location: getStopName(maxStopCode),
        timeHHMM: minsToHHMM(maxArrMin),
        durationMin: maxArrMin - busDepMin
      });
      arrivalMin = maxArrMin;
    }
  }

  const lastStep = steps[steps.length - 1];
  const totalMinutes = arrivalMin - leaveMin;

  return {
    steps,
    totalMinutes,
    arrivalHHMM: lastStep && lastStep.timeHHMM ? lastStep.timeHHMM : "--:--",
    primaryConnection
  };
}
